import { Link } from "react-router-dom";
import { Palette, RefreshCcw } from "lucide-react";

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                <RefreshCcw className="w-6 h-6 text-violet-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Refund Policy
              </h1>
            </div>
            
            <p className="text-slate-500 mb-8">Last updated: January 26, 2025</p>

            <div className="prose prose-slate max-w-none">
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">1. Satisfaction Guarantee</h2>
                <p className="text-slate-600 leading-relaxed">
                  We want you to be completely satisfied with ColorWonder AI. If you're not happy with our service, 
                  we offer a 7-day money-back guarantee for new subscriptions.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">2. Eligibility for Refunds</h2>
                <p className="text-slate-600 leading-relaxed mb-4">You may be eligible for a refund if:</p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>You request a refund within 7 days of your initial subscription purchase</li>
                  <li>You have generated fewer than 10 pages during this period</li>
                  <li>You have not previously received a refund from ColorWonder AI</li>
                  <li>There are technical issues preventing you from using the service</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">3. Non-Refundable Items</h2>
                <p className="text-slate-600 leading-relaxed mb-4">The following are NOT eligible for refunds:</p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Subscriptions after the 7-day guarantee period</li>
                  <li>Partial refunds for unused portion of subscription period</li>
                  <li>Accounts terminated for Terms of Service violations</li>
                  <li>Requests made after subscription renewal</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">4. How to Request a Refund</h2>
                <p className="text-slate-600 leading-relaxed mb-4">To request a refund:</p>
                <ol className="list-decimal pl-6 text-slate-600 space-y-2">
                  <li>Email us at <a href="mailto:support@colorwonder.app" className="text-violet-600 hover:underline">support@colorwonder.app</a></li>
                  <li>Include your account email and reason for the refund request</li>
                  <li>We will review your request within 2-3 business days</li>
                  <li>Approved refunds are processed within 5-10 business days</li>
                </ol>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">5. Refund Processing</h2>
                <p className="text-slate-600 leading-relaxed">
                  Approved refunds will be credited to your original payment method. Please note that your bank or 
                  credit card company may take additional time to process the refund and reflect it in your account.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">6. Subscription Cancellation</h2>
                <p className="text-slate-600 leading-relaxed">
                  You can cancel your subscription at any time from your dashboard. Cancellation will prevent future 
                  charges, and you'll continue to have access until the end of your current billing period. 
                  Cancellation does not automatically qualify you for a refund.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">7. Disputes</h2>
                <p className="text-slate-600 leading-relaxed">
                  If you believe you were incorrectly charged or have billing concerns, please contact us before 
                  disputing the charge with your bank. We're committed to resolving issues quickly and fairly.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">8. Contact Us</h2>
                <p className="text-slate-600 leading-relaxed">
                  For refund requests or billing questions, contact us at:{" "}
                  <a href="mailto:support@colorwonder.app" className="text-violet-600 hover:underline">
                    support@colorwonder.app
                  </a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
